/* prettier-ignore */

import { computed, inject, Injectable, NgZone, signal } from '@angular/core';
import { ScrollService } from './scrollContainer.service';
import { DataChatService } from './dataChat.service';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';

@Injectable({ providedIn: 'root' })

export class ChatBotTextAnimationService {
  private dataChat = inject(DataChatService);
  private scrollService = inject(ScrollService);
  private translateService = inject(TranslateService);
  private zone = inject(NgZone);

  // ---- Estado por completion_id
  private queues = new Map<string, string[]>();     // cola de chars por id
  private rafIds = new Map<string, number>();       // requestAnimationFrame id por id
  private nextAt = new Map<string, number>();       // timestamp para el próximo char por id
  private lastScrollAt = 0;

  // ---- Velocidad configurable (global)
  private msPerChar = 45;               // valor por defecto ~18 cps
  private punctuationPauseMs = 220;     // pausa natural tras .,;:!?…
  private punctuationRegex = /[.,;:!?…]/;

  private allIdleSubject = new Subject<void>();
  public allIdle$ = this.allIdleSubject.asObservable();
  private idleTimer: any = null;

  /** Cambia la velocidad de tecleo en caliente */
  public setTypingSpeed(onlyText: boolean = false) {
    if (onlyText){
      this.msPerChar = 15;
      this.punctuationPauseMs = 60;
      return;
    }

    this.msPerChar = 45;
    this.punctuationPauseMs = 220;
  }

  public enqueueText(id: string, text: string, onlyText: boolean = false) {
    if (!text) 
      return;

    this.setTypingSpeed(onlyText);
    
    if (!this.queues.has(id)) 
      this.queues.set(id, []);

    const q = this.queues.get(id)!;
    for (const ch of text) 
      q.push(ch);

    if (!this.rafIds.has(id)) 
      this.startTypingLoop(id, onlyText);
  }

  /** Bucle de tecleo letra a letra con rAF y control temporal */
  private startTypingLoop(id: string, onlyText: boolean) {
    this.nextAt.delete(id); // para arrancar “ya” en el primer frame

    const tick = (now: number) => {
      const q = this.queues.get(id);

      if (!q || q.length === 0) {
        const rafId = this.rafIds.get(id);
        if (rafId != null) cancelAnimationFrame(rafId);
        this.rafIds.delete(id);
        this.nextAt.delete(id);
        this.zone.run(() => {
          this.throttledScroll();
          this.maybeEmitAllIdle();
        });
        return;
      }

      // Inicializa el "cuando" para el primer char
      if (!this.nextAt.has(id)) 
        this.nextAt.set(id, now);

      let when = this.nextAt.get(id)!;
      let chunk = '';
      let emitted = 0;
      while (q.length > 0 && now >= when) {
        const ch = q.shift()!;
        chunk += ch;
        emitted++;

        // calcular delay del siguiente carácter
        let delay = this.msPerChar;
        const next = q[0];
        if (this.punctuationRegex.test(ch) && (next === ' ' || next === '\n' || next === '\r' || next === undefined)) {
          delay += this.punctuationPauseMs;
        }
        when += delay;
      }

      // Si hay algo que añadir, haz un único update
      if (emitted > 0) {
        const chunkToAppend = chunk; // cierra sobre el valor
        this.zone.run(() => {
          this.handleAssistenQueueMessage(id, chunkToAppend);
          this.throttledScroll();
        });
        this.nextAt.set(id, when);
      }

      // reprogramar
      const rafId = requestAnimationFrame(tick);
      this.rafIds.set(id, rafId);
    };

    this.zone.runOutsideAngular(() => {
      const rafId = requestAnimationFrame(tick);
      this.rafIds.set(id, rafId);
    });
  }

  private maybeEmitAllIdle() {
    if (this.idleTimer) {
      clearTimeout(this.idleTimer);
      this.idleTimer = null;
    }

    const scheduleCheck = () => {
      const anyQueueHasItems = [...this.queues.values()].some(q => q.length > 0);
      const anyRAF = this.rafIds.size > 0;
      if (!anyQueueHasItems && !anyRAF) 
        this.allIdleSubject.next();
    };

    requestAnimationFrame(() => {
      this.idleTimer = setTimeout(scheduleCheck, 40);
    });
  }

  /** Scroll con throttle para no forzar layout por letra */
  private throttledScroll() {
    const now = performance.now();
    if (now - this.lastScrollAt > 48) { // ~20fps
      this.scrollToBottom();
      this.lastScrollAt = now;
    }
  }

  createUserMessage(message: string) {
    if (!this.dataChat.isStreaming()){
      this.dataChat.messages.update((prev) => {
        const penultimateIndex = Math.max(0, prev.length - 1);
        return [
          ...prev.slice(0, penultimateIndex),
          { role: 'user', content: message, show: true },
          ...prev.slice(penultimateIndex),
        ];
      });
      this.scrollToBottom();
      return;
    }
    this.dataChat.messages.update((prev) => [
      ...prev, { role: 'user', content: message, show: true },
    ]);
    this.scrollToBottom();
  }

  handleAssistenQueueMessage(id: string, text: string){
    this.dataChat.messages.update((prev) => {
      const index = prev.findIndex((m: any) => m.id === id);
      if (index !== -1) {
        const updated = [...prev];
        const target = updated[index];
        updated[index] = { ...target, content: (target.content ?? '') + text };
        return updated;
      }
      return [...prev, { role: 'assistant', content: text, show: true, id }];
    });
  }

  handleAssistenAllMessage(id: string, text: string){
    this.dataChat.messages.update((prev) => {
      const index = prev.findIndex((m: any) => m.id === id);
      if (index !== -1) {
        const updated = [...prev];
        const targetMessage = updated[index];
        updated[index] = { ...targetMessage, content: text };
        return updated;
      }
      return prev;
    });
    this.scrollToBottom();
  }

  handleAllTextOnStop(text: string){
    this.dataChat.messages.update((prev) => {
      const lastAssistantIndex = [...prev].reverse().findIndex((m) => m.role === 'assistant');
      if (lastAssistantIndex !== -1) {
        const actualIndex = prev.length - 1 - lastAssistantIndex;
        const updated = [...prev];
        updated[actualIndex] = { ...updated[actualIndex], content: text, show: true };
        return updated;
      }
      return prev;
    });
    this.scrollToBottom();
  }

  showConnectionErrorMessage(){
    if (this.dataChat?.messages()?.[this.dataChat?.messages()?.length - 1].danger) 
      return;
    
    this.dataChat.messages.update((prev) => [
      ...prev,
      {
        content: this.translateService.instant('Fallo en la comunicación, vuelve a intentarlo'),
        role: 'assistant',
        show: true,
        danger: true,
      },
    ]);
  }

  public cancel(id: string) {
    const rafId = this.rafIds?.get(id);
        if (rafId != null) {
        cancelAnimationFrame(rafId);
        this.rafIds.delete(id);
    }

    this.queues?.delete(id);
    this.nextAt?.delete(id);
    this.zone.run(() => this.maybeEmitAllIdle());
  }

  public scrollToBottom(){
    setTimeout(() => {
      this.scrollService.scrollToBottom();
    }, 0);
  }

}